import { Component, Input, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import {
  buttonConfig,
  dropdownConfig,
  onFormFieldChangeType,
  textInputConfig,
} from '@core/types/types';
import { TranslateModule } from '@ngx-translate/core';
import {
  bootstrapSearch,
  bootstrapThreeDotsVertical,
} from '@ng-icons/bootstrap-icons';
import { NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { CustomButtonComponent } from '@shared/components/button/button.component';
import { IconsComponent } from '@shared/components/icons/icon.component';
import { CustomTextInputComponent } from '@shared/components/text-input/text-input.component';
import { debounceTime, distinctUntilChanged, filter, map, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { spinnerSvgIcon } from '@assets/icons/icons';

@Component({
  imports: [
    TranslateModule,
    NgbDropdownModule,
    CustomButtonComponent,
    IconsComponent,
    CustomTextInputComponent,
  ],
  standalone: true,
  selector: 'dropdown-component',
  styleUrl: './dropdown.component.scss',
  templateUrl: './dropdown.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class CustomDropdownComponent {
  @ViewChild('myDrop', { static: false }) myDrop!: NgbDropdown;

  @Input({ required: true }) config!: dropdownConfig;
  @Input({ required: false }) data!: any;

  protected icons = {
    threeDotsVertical: bootstrapThreeDotsVertical,
    search: bootstrapSearch,
    spinner: spinnerSvgIcon,
  };

  protected filteredData?: any[] = undefined;

  protected defaultBtnConfig: buttonConfig = {
    type: 'icon',
    icon: this.icons.threeDotsVertical,
    color: 'info',
    theme: 'badge',
  };

  protected textBox = {
    inputType: 'textbox',
    fieldName: 'identificationNumber',
    type: 'text',
    placeholder: {
      key: 'app.pageId.assisted-patient.select-companion-modal.register-form.national-identification-number.placeholder',
    },
    required: true,
  } as textInputConfig;

  constructor(private readonly http: HttpClient) {}

  ngOnInit() {
    if (this.config.type == 'dropdown' && !this.config.customBtn)
      this.config.customBtn = this.defaultBtnConfig;
  }

  getOptionLabel(option: any) {
    if (this.config.type !== 'searchBox') return '';

    const cfg = this.config.config;

    const { labelKey } = cfg;

    if (!labelKey.includes('-') && !labelKey.includes('/')) {
      return option[labelKey] ?? '';
    }

    const groups = labelKey.split('-');

    const labels = groups.map((group) => {
      const subKeys = group.split('/');
      const subLabel = subKeys
        .map((subKey) => option[subKey]?.toString().trim() ?? '')
        .filter(Boolean)
        .join(' ');
      return subLabel.trim();
    });

    return labels.filter(Boolean).join(' - ');
  }

  filterData(e: onFormFieldChangeType) {
    if (this.myDrop.isOpen()) this.myDrop.close();

    if (this.config.type !== 'searchBox') return;

    if (!e.value) {
      this.filteredData = undefined;
      return;
    }

    const cfg = this.config.config;

    if (cfg.type == 'local') {
      const data$ = of(cfg.data);
      data$
        .pipe(
          debounceTime(200),
          distinctUntilChanged(),
          map((arr) =>
            arr.filter((item) =>
              item[cfg.searchKey]?.toLowerCase().includes(e.value.toLowerCase())
            )
          )
        )
        .subscribe({
          next: (filtered) => {
            this.filteredData = filtered;

            this.myDrop.open();
          },
        });
    } else {
      this.filteredData = undefined;
      this.myDrop.open();

      this.http
        .get(`${cfg.url}`, {
          params: { ...(cfg.params ?? {}), [cfg.searchKey]: e.value },
          headers: cfg.headers ?? {},
        })
        .subscribe({
          next: (res: any) => {
            this.filteredData = res.data;
            this.myDrop.open();
          },
        });
    }
  }

  handleSelectedData(data: any) {
    if (this.config.type !== 'searchBox') return;
    this.config.config.callbackFunction(data);
  }
}
